import React, { useCallback, useEffect, useState } from 'react';
import { moderationApi, ReportAction, ReportDetail, UserReport } from '../../../services/moderationApi';
import { isPlaceholderEmail } from '../../../utils/phoneDisplayUtils';
import { CONTEXT_LABELS, RESOLUTION_LABELS, TYPE_LABELS, getInitials } from './labels';
import { ReportActions } from './ReportActions';

interface ReportDetailsProps {
  report: UserReport | null;
  /** Called after an action changes the report, so the list can reload. */
  onChanged: () => void;
}

const Person: React.FC<{ title: string; person?: UserReport['reporter']; tone: string }> = ({ title, person, tone }) => (
  <div>
    <h4 className="text-sm font-medium text-gray-500 mb-2">{title}</h4>
    <div className={`flex items-center gap-3 p-3 rounded-lg ${tone}`}>
      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
        <span className="font-medium text-sm">{getInitials(person?.full_name)}</span>
      </div>
      <div>
        <p className="font-medium text-sm">{person?.full_name || 'Unknown user'}</p>
        {!isPlaceholderEmail(person?.email) && <p className="text-xs text-gray-500">{person?.email}</p>}
      </div>
    </div>
  </div>
);

export const ReportDetails: React.FC<ReportDetailsProps> = ({ report, onChanged }) => {
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reportId = report?.id;

  const load = useCallback(async () => {
    if (!reportId) return;
    setError(null);
    const result = await moderationApi.getReportDetails(reportId);
    if (result.success && result.data) setDetail(result.data);
    else setError(result.error || 'Failed to load report');
  }, [reportId]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  if (!report) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
        Select a report to view details
      </div>
    );
  }

  const current = detail?.report.id === report.id ? detail.report : report;
  const reportedName = current.reported_user?.full_name || 'this user';

  const handleAction = async (action: ReportAction, options: { notes?: string; circleIds?: string[] }) => {
    setBusy(true);
    const result = await moderationApi.reviewReport(report.id, action, options);
    setBusy(false);
    if (!result.success) return alert(result.error || 'Failed to process action');
    await load();
    onChanged();
  };

  const liftBan = async (circleId: string, circleName: string) => {
    if (!confirm(`Let ${reportedName} rejoin ${circleName}?`)) return;
    setBusy(true);
    const result = await moderationApi.unbanFromCircle(circleId, current.reported_user_id);
    setBusy(false);
    if (!result.success) return alert(result.error || 'Failed to lift ban');
    await load();
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold">Report Details</h3>
      </div>
      <div className="p-6 space-y-6">
        {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md text-sm">{error}</div>}
        <Person title="Reported user" person={current.reported_user} tone="bg-red-50" />
        <Person title="Reported by" person={current.reporter} tone="bg-gray-50" />

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><h4 className="font-medium text-gray-500 mb-1">Type</h4>{TYPE_LABELS[current.report_type]}</div>
          <div><h4 className="font-medium text-gray-500 mb-1">Context</h4>{CONTEXT_LABELS[current.report_context]}</div>
          <div><h4 className="font-medium text-gray-500 mb-1">Filed</h4>{new Date(current.created_at).toLocaleString()}</div>
          <div>
            <h4 className="font-medium text-gray-500 mb-1">Outcome</h4>
            {current.resolution ? RESOLUTION_LABELS[current.resolution] : 'None yet'}
          </div>
        </div>

        {current.description && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
            <p className="text-sm bg-gray-50 p-3 rounded-lg">{current.description}</p>
          </div>
        )}
        {current.admin_notes && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Reviewer notes</h4>
            <p className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-line">{current.admin_notes}</p>
          </div>
        )}

        {detail && (
          <>
            <div className="text-sm">
              <h4 className="font-medium text-gray-500 mb-1">Other reports about {reportedName}</h4>
              {detail.prior_reports.length === 0 ? 'None' : (
                <ul className="space-y-1">
                  {detail.prior_reports.map(prior => (
                    <li key={prior.id}>
                      {new Date(prior.created_at).toLocaleDateString()}: {TYPE_LABELS[prior.report_type]}
                      {' '}({prior.resolution ? RESOLUTION_LABELS[prior.resolution] : prior.status})
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {detail.circle_bans.length > 0 && (
              <div className="text-sm">
                <h4 className="font-medium text-gray-500 mb-1">Currently banned from</h4>
                <ul className="space-y-1">
                  {detail.circle_bans.map(ban => (
                    <li key={ban.circle_id} className="flex items-center justify-between">
                      {ban.circle?.name || 'Unknown circle'}
                      <button
                        onClick={() => liftBan(ban.circle_id, ban.circle?.name || 'this circle')}
                        disabled={busy}
                        className="text-blue-600 hover:underline disabled:opacity-50"
                      >
                        Lift ban
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ReportActions
              reportedName={reportedName}
              reportedUserId={current.reported_user_id}
              sharedCircles={detail.shared_circles}
              busy={busy}
              onAction={handleAction}
            />
          </>
        )}
      </div>
    </div>
  );
};
